import { ref, update } from "firebase/database";
import { realtimeDb } from "../firebase/config";
function timeSeed(value) {
    if (value == null || value === "")
        return null;
    const normalized = String(value).trim().toUpperCase();
    if (normalized === "DQ" || normalized === "DNF" || normalized === "DNS")
        return 99999;
    const [whole, fraction = ""] = normalized.split(".");
    const parts = whole.split(":").map(Number);
    if (parts.some((part) => !Number.isFinite(part)))
        return null;
    const seconds = parts.length === 2 ? parts[0] * 60 + parts[1] : parts[0];
    return seconds + (fraction ? Number(`0.${fraction}`) : 0);
}
function fieldSeed(value) {
    if (value == null || value === "")
        return null;
    const normalized = String(value).trim().toUpperCase();
    if (normalized === "FOUL" || normalized === "PASS" || normalized === "NH")
        return -1;
    const imperial = String(value).match(/^(\d+)-(\d{1,2}(?:\.\d+)?)$/);
    if (imperial)
        return Math.round((Number(imperial[1]) + Number(imperial[2]) / 12) * 3048) / 10000;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}
export function seedForResult(value, fieldEvent = false) {
    return fieldEvent ? fieldSeed(value) : timeSeed(value);
}
function compactObject(value) {
    if (!value)
        return null;
    const compact = Object.fromEntries(Object.entries(value).filter(([, entry]) => entry != null && entry !== ""));
    return Object.keys(compact).length > 0 ? compact : null;
}
function meetMetadata(updates, context, athleteMeetPath) {
    updates[`${athleteMeetPath}/name`] = context.meetName;
    updates[`${athleteMeetPath}/dateTime`] = context.meetDateTime;
    updates[`${athleteMeetPath}/location`] = context.meetLocation;
    updates[`${athleteMeetPath}/course`] = context.meetCourse ?? null;
}
export async function saveIndividualResult(context, result) {
    if (!result.id || !result.athlete?.id)
        throw new Error("This result is missing its athlete or result ID.");
    const meetResultPath = `competitions/${context.teamId}/${context.seasonId}/results/${context.meetId}/${context.eventId}/results/${result.id}`;
    const athleteMeetPath = `athletes/${context.teamId}/${result.athlete.id}/${context.seasonId}/${context.meetId}`;
    const athleteResultPath = `${athleteMeetPath}/results/${result.id}`;
    const times = compactObject(result.times ? { trial: result.times.trial, final: result.times.final } : null);
    const attempts = Array.isArray(result.attempts) ? result.attempts.filter((entry) => entry != null && entry !== "") : result.attempts ?? null;
    const storedAttempts = Array.isArray(attempts) ? (attempts.length > 0 ? attempts : null) : attempts;
    const splits = result.splits?.filter((split) => split.time != null && split.time !== "") ?? null;
    const multi = compactObject(result.multi);
    const normalizedResult = result.result ?? null;
    const seed = seedForResult(normalizedResult, context.fieldEvent);
    const updates = {
        [`${meetResultPath}/athlete/firstName`]: result.athlete.firstName ?? null,
        [`${meetResultPath}/athlete/lastName`]: result.athlete.lastName ?? null,
        [`${meetResultPath}/result`]: normalizedResult,
        [`${meetResultPath}/seed`]: seed,
        [`${meetResultPath}/comment`]: result.comment?.trim() || null,
        [`${meetResultPath}/attempts`]: storedAttempts,
        [`${meetResultPath}/times`]: times,
        [`${meetResultPath}/splits`]: splits && splits.length > 0 ? splits : null,
        [`${meetResultPath}/multi`]: multi,
        [`${meetResultPath}/hand`]: result.hand === true ? true : null,
    };
    if (normalizedResult != null && normalizedResult !== "") {
        meetMetadata(updates, context, athleteMeetPath);
        updates[athleteResultPath] = {
            name: context.eventName,
            result: normalizedResult,
            seed,
            comment: result.comment?.trim() || null,
            attempts: storedAttempts,
            times,
            splits: splits && splits.length > 0 ? splits : null,
            multi,
            hand: result.hand === true ? true : null,
        };
    }
    else {
        updates[athleteResultPath] = null;
    }
    await update(ref(realtimeDb), updates);
}
export async function saveRelayResult(context, result, removedAthleteIds = []) {
    if (!result.id)
        throw new Error("This relay result is missing its result ID.");
    const meetResultPath = `competitions/${context.teamId}/${context.seasonId}/results/${context.meetId}/${context.eventId}/results/${result.id}`;
    const times = compactObject(result.times ? { trial: result.times.trial, final: result.times.final } : null);
    const seed = seedForResult(result.result);
    const updates = {
        [`${meetResultPath}/result`]: result.result ?? null,
        [`${meetResultPath}/seed`]: seed,
        [`${meetResultPath}/comment`]: result.comment?.trim() || null,
        [`${meetResultPath}/team`]: result.team?.trim() || null,
        [`${meetResultPath}/times`]: times,
        [`${meetResultPath}/hand`]: result.hand === true ? true : null,
        [`${meetResultPath}/relay`]: result.relay ?? null,
    };
    Object.entries(result.relay ?? {}).forEach(([leg, legValue]) => {
        const athlete = legValue.athlete;
        if (!athlete?.id)
            return;
        const athleteMeetPath = `athletes/${context.teamId}/${athlete.id}/${context.seasonId}/${context.meetId}`;
        const athleteResultPath = `${athleteMeetPath}/results/${result.id}`;
        if (result.result != null && result.result !== "") {
            meetMetadata(updates, context, athleteMeetPath);
            updates[athleteResultPath] = { name: context.eventName, result: result.result, seed, comment: result.comment?.trim() || null, relay: { leg, split: legValue.split ?? null }, hand: result.hand === true ? true : null };
        }
        else
            updates[athleteResultPath] = null;
    });
    removedAthleteIds.forEach((athleteId) => { updates[`athletes/${context.teamId}/${athleteId}/${context.seasonId}/${context.meetId}/results/${result.id}`] = null; });
    await update(ref(realtimeDb), updates);
}
export async function deleteEventResult(context, result) {
    if (!result.id)
        throw new Error("This result is missing its result ID.");
    const updates = {
        [`competitions/${context.teamId}/${context.seasonId}/results/${context.meetId}/${context.eventId}/results/${result.id}`]: null,
    };
    const athletes = result.athlete ? [result.athlete] : Object.values(result.relay ?? {}).map((leg) => leg.athlete).filter((athlete) => Boolean(athlete?.id));
    athletes.forEach((athlete) => { if (athlete.id)
        updates[`athletes/${context.teamId}/${athlete.id}/${context.seasonId}/${context.meetId}/results/${result.id}`] = null; });
    await update(ref(realtimeDb), updates);
}
